import { FilePreviewer as FilePreviewComp } from "../FilePreviewer";
import { getFileType } from "@certa/common/utils/file";
import {
  fileDownload,
  FileDownloadTypes
} from "@certa/common/utils/fileDownload";
import type { FilePreviewDetails } from "./FilePreviewerPage.types";
import {
  Typography,
  TypographyColors
} from "@certa/catalyst/components/Typography";
import { useIntl } from "react-intl";
import { css } from "emotion";
import DOMPurify from "dompurify";

export type FilePreviewerPageProps = {
  hasURL: boolean;
} & FilePreviewDetails;

const useExtractFilePreviewerPropsFromUrl = (): FilePreviewerPageProps => {
  // Get the search params from the URL
  const searchParams = new URLSearchParams(window.location.search);

  const dataSource = searchParams.get("dataSource");

  const safeUrl = DOMPurify.sanitize(
    decodeURIComponent(searchParams.get("url") || "")
  );

  // Decode URL parameters
  return {
    hasURL: !!searchParams.get("url"),
    url: safeUrl,
    fileName: decodeURIComponent(searchParams.get("fileName") || ""),
    highlightedText: searchParams.has("highlightedText")
      ? decodeURIComponent(searchParams.get("highlightedText") || "")
      : undefined,
    filePreviewDataSource: dataSource === "mimirV1" ? "mimirV1" : "certaV2"
  };
};

export const FilePreviewerPage = () => {
  const { hasURL, url, fileName, highlightedText, filePreviewDataSource } =
    useExtractFilePreviewerPropsFromUrl();
  const fileType = getFileType(fileName);
  const isPdf = fileType === "pdf";
  const intl = useIntl();

  const onDownload = () => {
    fileDownload({
      downloadType: FileDownloadTypes.FILE_DOWNLOAD,
      url,
      fileName
    });
  };

  return (
    <div className={pageStyles}>
      {hasURL ? (
        <FilePreviewComp
          url={url}
          disablePreview={isPdf && !(filePreviewDataSource === "mimirV1")}
          fileName={fileName}
          highlightedText={highlightedText}
          filePreviewDataSource={filePreviewDataSource}
          onDownload={onDownload}
          isFullScreen={true}
          showToolbar={true}
        />
      ) : (
        <Typography color={TypographyColors.NEUTRAL_100}>
          {intl.formatMessage({
            id: "filePreview.noFileFound",
            defaultMessage: "We couldn't find the file you're looking for."
          })}
        </Typography>
      )}
    </div>
  );
};

const pageStyles = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100vh",
  backgroundColor: "rgb(82, 86, 89)"
});
